import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Moon,
  Phone,
  RefreshCw,
  Route,
  Sun,
  Truck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import BottomNavigation from "@/components/BottomNavigation";
import { useDriver } from "@/context/DriverContext";
import { tripService } from "@/services/api";
import { getArgentinaDate, getArgentinaTomorrowDate } from "@/utils/dateUtils";
import { toast } from "@/hooks/use-toast";
import { MyCoordinationShipment, MyCoordinationUncalledDriver } from "@/types";
import {
  groupCoordinations,
  uncalledSections,
  ShiftGroup,
  SvcShiftGroup,
  UncalledSection,
  UnitTypeGroup,
} from "@/utils/coordinationGrouping";

type DayKey = "today" | "tomorrow";
type ViewKey = "citados" | "no_citados";

function statusBadgeClass(status: string): string {
  switch (status) {
    case "PENDIENTE":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "CONFIRMADO":
      return "bg-green-100 text-green-800 border-green-300";
    case "EN_CURSO":
      return "bg-primary-soft text-primary-soft-foreground border-primary/30";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
}

function ShipmentCard({ shipment }: { shipment: MyCoordinationShipment }) {
  const name = shipment.driver
    ? `${shipment.driver.firstName} ${shipment.driver.lastName}`.trim() || "Sin chofer"
    : "Sin chofer";
  const phone = shipment.driver?.phone ?? null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-gray-900 tabular-nums">
              {shipment.time ? shipment.time.slice(0, 5) : "--:--"}
            </span>
            <span
              className={cn(
                "font-semibold truncate",
                shipment.driver ? "text-gray-900" : "text-red-600"
              )}
            >
              {name}
            </span>
          </div>
          <div className="text-sm text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
            <span>
              <span className="font-medium">Patente:</span> {shipment.vehiclePlate ?? "—"}
            </span>
            {shipment.conditionName && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                {shipment.conditionName}
              </span>
            )}
          </div>
          {shipment.structureCode && (
            <div className="text-xs text-gray-500 font-mono truncate mt-0.5">
              {shipment.structureCode}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border",
              statusBadgeClass(shipment.status)
            )}
          >
            {shipment.status}
          </span>
          {phone && (
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary-soft px-2.5 py-1.5 text-primary text-sm font-medium active:bg-primary/15"
            >
              <Phone className="h-4 w-4" /> Llamar
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function UnitTypeSection({ group }: { group: UnitTypeGroup }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 active:bg-gray-100"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Truck className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-800 truncate">
            {group.unitTypeName ?? "Sin tipo de unidad"}
          </span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5">
            {group.shipments.length}
          </span>
          <ChevronDown
            className={cn("h-4 w-4 text-gray-400 transition-transform", open && "rotate-180")}
          />
        </span>
      </button>
      {open && (
        <div className="p-2 space-y-2 bg-gray-50/50">
          {group.shipments.map((s) => (
            <ShipmentCard key={s.id} shipment={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function SvcSection({ group }: { group: SvcShiftGroup }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-3 active:bg-gray-50"
      >
        <span className="min-w-0 text-left">
          <span className="block font-bold text-gray-900 truncate">{group.svc.name}</span>
          <span className="block text-xs text-gray-500">{group.svc.code}</span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          {group.unassigned > 0 && (
            <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
              {group.unassigned} sin chofer
            </span>
          )}
          <span className="text-xs font-medium text-primary bg-primary-soft border border-primary/30 rounded-full px-2 py-0.5">
            {group.total} {group.total === 1 ? "ruta" : "rutas"}
          </span>
          <ChevronDown
            className={cn("h-5 w-5 text-gray-400 transition-transform", open && "rotate-180")}
          />
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-2">
          {group.unitTypes.map((u) => (
            <UnitTypeSection key={u.unitTypeName ?? "__sin_tipo__"} group={u} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShiftSection({ group, showHeader }: { group: ShiftGroup; showHeader: boolean }) {
  const isAM = group.shift === "AM";
  const label = group.shift ? `Turno ${group.shift}` : "Sin turno";

  return (
    <section className="space-y-2">
      {showHeader && (
        <div className="flex items-center gap-2 pt-1">
          {isAM ? (
            <Sun className="h-5 w-5 text-amber-500" />
          ) : (
            <Moon className="h-5 w-5 text-indigo-500" />
          )}
          <h2 className="text-lg font-bold text-gray-900">{label}</h2>
          <span className="text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5">
            {group.total} {group.total === 1 ? "ruta" : "rutas"}
          </span>
        </div>
      )}
      {group.svcs.map((svcGroup) => (
        <SvcSection key={`${group.shift ?? "none"}-${svcGroup.svc.id}`} group={svcGroup} />
      ))}
    </section>
  );
}

function shiftBadgeClass(shift: string | null): string {
  switch (shift) {
    case "AM":
      return "bg-amber-100 text-amber-800 border-amber-300";
    case "PM":
      return "bg-indigo-100 text-indigo-800 border-indigo-300";
    default:
      return "bg-gray-100 text-gray-700 border-gray-300";
  }
}

function UncalledDriverCard({ driver }: { driver: MyCoordinationUncalledDriver }) {
  const name = `${driver.firstName} ${driver.lastName}`.trim() || "Sin nombre";

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-gray-900 truncate block">{name}</span>
        <span
          className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border mt-1",
            shiftBadgeClass(driver.shift)
          )}
        >
          {driver.shift ?? "Sin turno"}
        </span>
      </div>
      {driver.phone && (
        <a
          href={`tel:${driver.phone}`}
          className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary-soft px-2.5 py-1.5 text-primary text-sm font-medium active:bg-primary/15 flex-shrink-0"
        >
          <Phone className="h-4 w-4" /> Llamar
        </a>
      )}
    </div>
  );
}

function UncalledSvcSection({ section }: { section: UncalledSection }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-3 active:bg-gray-50"
      >
        <span className="min-w-0 text-left">
          <span className="block font-bold text-gray-900 truncate">{section.svc.name}</span>
          <span className="block text-xs text-gray-500">{section.svc.code}</span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">
            {section.drivers.length} sin citar
          </span>
          <ChevronDown
            className={cn("h-5 w-5 text-gray-400 transition-transform", open && "rotate-180")}
          />
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-2">
          {section.drivers.map((d) => (
            <UncalledDriverCard key={`${d.id}-${d.shift ?? ""}`} driver={d} />
          ))}
        </div>
      )}
    </div>
  );
}

const Coordination = () => {
  const navigate = useNavigate();
  const {
    todayCoordinations,
    tomorrowCoordinations,
    setTodayCoordinations,
    setTomorrowCoordinations,
  } = useDriver();

  const hasToday = (todayCoordinations?.length ?? 0) > 0;
  const hasTomorrow = (tomorrowCoordinations?.length ?? 0) > 0;
  const isLoaded = todayCoordinations !== null && tomorrowCoordinations !== null;
  const showSwitch = hasToday && hasTomorrow;

  const [day, setDay] = useState<DayKey>("today");
  const [view, setView] = useState<ViewKey>("citados");
  const [refreshing, setRefreshing] = useState(false);

  // Una vez cargados, ajustar el día inicial al que corresponda.
  useEffect(() => {
    if (!isLoaded) return;
    if (!hasToday && hasTomorrow) setDay("tomorrow");
    // si hasToday (con o sin mañana) o ambos vacíos → queda en "today"
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si no coordina ningún día → redirect a Home con toast.
  useEffect(() => {
    if (isLoaded && !hasToday && !hasTomorrow) {
      toast({
        title: "Sin coordinaciones",
        description: "No tenés coordinaciones asignadas hoy ni mañana.",
      });
      navigate("/", { replace: true });
    }
  }, [isLoaded, hasToday, hasTomorrow, navigate]);

  const visibleGroups = useMemo(() => {
    return day === "today" ? todayCoordinations ?? [] : tomorrowCoordinations ?? [];
  }, [day, todayCoordinations, tomorrowCoordinations]);

  const shiftGroups = useMemo(() => groupCoordinations(visibleGroups), [visibleGroups]);
  const showShiftHeaders = shiftGroups.length > 1;

  const uncalledList = useMemo(() => uncalledSections(visibleGroups), [visibleGroups]);
  const citedCount = useMemo(
    () => shiftGroups.reduce((acc, g) => acc + g.total, 0),
    [shiftGroups]
  );
  const uncalledCount = useMemo(
    () => uncalledList.reduce((acc, s) => acc + s.drivers.length, 0),
    [uncalledList]
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const dateStr = day === "today" ? getArgentinaDate() : getArgentinaTomorrowDate();
      const response = await tripService.getMyCoordinations(dateStr);
      const data = response.data?.data;
      const groups = Array.isArray(data) ? data : [];
      if (day === "today") setTodayCoordinations(groups);
      else setTomorrowCoordinations(groups);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron actualizar las coordinaciones.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      <header className="bg-primary text-white p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <h1 className="text-xl font-bold">Coordinación</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-white"
          >
            <RefreshCw className={cn("h-5 w-5", refreshing && "animate-spin")} />
          </Button>
        </div>
      </header>

      <main className="p-4 max-w-full space-y-4">
        {showSwitch ? (
          <div className="flex bg-white rounded-lg border border-gray-200 p-1 gap-1">
            <button
              type="button"
              onClick={() => setDay("today")}
              className={cn(
                "flex-1 py-2 rounded-md text-sm font-medium transition-colors",
                day === "today" ? "bg-primary text-white" : "text-gray-700"
              )}
            >
              Hoy
            </button>
            <button
              type="button"
              onClick={() => setDay("tomorrow")}
              className={cn(
                "flex-1 py-2 rounded-md text-sm font-medium transition-colors",
                day === "tomorrow" ? "bg-primary text-white" : "text-gray-700"
              )}
            >
              Mañana
            </button>
          </div>
        ) : (
          <div className="text-sm text-gray-600 font-medium">
            {day === "today" ? "Hoy" : "Mañana"}
          </div>
        )}

        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setView("citados")}
            className={cn(
              "flex-1 pb-2 text-sm font-medium border-b-2 transition-colors",
              view === "citados"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500"
            )}
          >
            Citados{citedCount > 0 ? ` (${citedCount})` : ""}
          </button>
          <button
            type="button"
            onClick={() => setView("no_citados")}
            className={cn(
              "flex-1 pb-2 text-sm font-medium border-b-2 transition-colors",
              view === "no_citados"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500"
            )}
          >
            No citados{uncalledCount > 0 ? ` (${uncalledCount})` : ""}
          </button>
        </div>

        {!isLoaded ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center text-gray-500">
            Cargando coordinaciones…
          </div>
        ) : view === "citados" ? (
          shiftGroups.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
              <Route className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              No hay rutas para mostrar.
            </div>
          ) : (
            <div className="space-y-5">
              {shiftGroups.map((group) => (
                <ShiftSection
                  key={group.shift ?? "none"}
                  group={group}
                  showHeader={showShiftHeaders}
                />
              ))}
            </div>
          )
        ) : uncalledList.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
            <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            No hay choferes sin citar.
          </div>
        ) : (
          <div className="space-y-2">
            {uncalledList.map((section) => (
              <UncalledSvcSection key={section.svc.id} section={section} />
            ))}
          </div>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
};

export default Coordination;
